type RoleLike = {
  id?: string | number | null;
  name?: string | null;
  permissions?: string[] | string | null;
};

type StaffLike = {
  id?: string | number | null;
  role?: RoleLike | null;
};

type ValidateAction = "add" | "update" | "delete";

const ADMIN_ROLE = "ADMIN";
const NO_ACCESS_ROLE = "NO ACCESS";
const SENSITIVE_BUSINESS_PERMISSIONS = ["addBusiness", "editBusiness"];

const normalizeRoleName = (value: unknown = "") =>
  String(value || "").trim().toUpperCase();

const toPermissionsArray = (permissions: unknown): string[] => {
  if (Array.isArray(permissions)) {
    return permissions.filter(Boolean).map((item) => String(item));
  }
  if (typeof permissions === "string") {
    const trimmed = permissions.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed)
        ? parsed.filter(Boolean).map((item) => String(item))
        : [];
    } catch (_err) {
      return [];
    }
  }
  return [];
};

const findRoleInCatalog = (roles: RoleLike[] = [], role?: RoleLike | null) => {
  if (!Array.isArray(roles) || !role) return null;
  const roleId = role?.id != null ? String(role.id) : null;
  const roleName = normalizeRoleName(role?.name);
  return (
    roles.find((item) => roleId && String(item?.id) === roleId) ||
    roles.find((item) => normalizeRoleName(item?.name) === roleName) ||
    null
  );
};

const countAdminStaff = (staffs: StaffLike[] = []) =>
  (Array.isArray(staffs) ? staffs : []).filter(
    (staff) => normalizeRoleName(staff?.role?.name) === ADMIN_ROLE
  ).length;

export function validateRoleCatalogChecklist(roles: RoleLike[] = []) {
  const errors: string[] = [];
  const warnings: string[] = [];
  const list = Array.isArray(roles) ? roles : [];

  if (!list.length) {
    errors.push("No roles configured for this business.");
  }

  const hasAdmin = list.some((role) => normalizeRoleName(role?.name) === ADMIN_ROLE);
  if (!hasAdmin) {
    errors.push("At least one Admin role must exist in role configuration.");
  }

  const noAccessRole = list.find(
    (role) => normalizeRoleName(role?.name) === NO_ACCESS_ROLE
  );
  if (noAccessRole) {
    const perms = toPermissionsArray(noAccessRole?.permissions);
    if (perms.length > 0) {
      warnings.push("No Access role should not include any permissions.");
    }
  }

  list.forEach((role) => {
    const roleName = normalizeRoleName(role?.name);
    if (!roleName || roleName === ADMIN_ROLE) return;
    const perms = toPermissionsArray(role?.permissions);
    const hasSensitiveBusinessPermission = SENSITIVE_BUSINESS_PERMISSIONS.some(
      (permission) => perms.includes(permission)
    );
    if (hasSensitiveBusinessPermission) {
      warnings.push(
        `${role?.name || "Role"} includes business-level edit permissions. Consider limiting add/edit business to Admin only.`
      );
    }
  });

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

export function validateStaffRoleChangeChecklist({
  staffs = [],
  roles = [],
  action = "update",
  targetStaff = null,
  nextRole = null,
}: {
  staffs?: StaffLike[];
  roles?: RoleLike[];
  action?: ValidateAction;
  targetStaff?: StaffLike | null;
  nextRole?: RoleLike | null;
}) {
  const errors: string[] = [];
  const warnings: string[] = [];
  const adminCount = countAdminStaff(staffs);
  const targetIsAdmin = normalizeRoleName(targetStaff?.role?.name) === ADMIN_ROLE;
  const nextIsAdmin = normalizeRoleName(nextRole?.name) === ADMIN_ROLE;
  const nextIsNoAccess = normalizeRoleName(nextRole?.name) === NO_ACCESS_ROLE;

  const catalogValidation = validateRoleCatalogChecklist(roles);
  warnings.push(...catalogValidation.warnings);
  if (!catalogValidation.valid) {
    errors.push(...catalogValidation.errors);
  }

  if (action === "delete" && targetIsAdmin && adminCount <= 1) {
    errors.push(
      "Cannot delete the last Admin user. Add another Admin first."
    );
  }

  if ((action === "add" || action === "update") && !nextRole) {
    errors.push("Staff role is required.");
  }

  if (nextRole && !findRoleInCatalog(roles, nextRole)) {
    errors.push("Selected role is not valid for this business.");
  }

  if (
    action === "update" &&
    targetIsAdmin &&
    !nextIsAdmin &&
    adminCount <= 1
  ) {
    errors.push(
      "Cannot change the last Admin user to a non-admin role. Add another Admin first."
    );
  }

  if ((action === "add" || action === "update") && nextIsNoAccess) {
    warnings.push(
      "No Access role will prevent the user from using protected features."
    );
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}
